import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import PersonalInfoStep from './steps/PersonalInfoStep';
import AIExperienceStep from './steps/AIExperienceStep';
import TechnicalInfoStep from './steps/TechnicalInfoStep';
import ExperienceStep from './steps/ExperienceStep';
import ReviewStep from './steps/ReviewStep';
import { submitCareersApplication } from '@/api/forms';

export interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  positionType: string;

  // AI Experience
  aiToolsUsed: string[];
  aiExperienceLevel: string;
  favoriteAITool: string;
  aiProjectDescription: string;

  // Technical Information
  githubUrl: string;
  linkedinUrl: string;
  portfolioUrl: string;
  technicalSkills: string[];
  preferredLanguages: string[];

  // Professional Experience
  currentRole: string;
  experience: string;
  education: string;
  certifications: string;
  whyJoinUs: string;
  availabilityDate: string;
  workPreference: string;
  salaryExpectation: string;
  coverLetter: string;
}

const CareersApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    positionType: '',
    aiToolsUsed: [],
    aiExperienceLevel: '',
    favoriteAITool: '',
    aiProjectDescription: '',
    githubUrl: '',
    linkedinUrl: '',
    portfolioUrl: '',
    technicalSkills: [],
    preferredLanguages: [],
    currentRole: '',
    experience: '',
    education: '',
    certifications: '',
    whyJoinUs: '',
    availabilityDate: '',
    workPreference: '',
    salaryExpectation: '',
    coverLetter: ''
  });

  const steps = [
    { title: 'Personal Info', component: PersonalInfoStep },
    { title: 'AI Experience', component: AIExperienceStep },
    { title: 'Technical Info', component: TechnicalInfoStep },
    { title: 'Experience', component: ExperienceStep },
    { title: 'Review', component: ReviewStep }
  ];

  const updateFormData = (data: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const validateCurrentStep = () => {
    console.log('Validating step:', currentStep);
    console.log('Form data:', formData);

    switch (currentStep) {
      case 0: // Personal Info
        console.log('Validating Personal Info step...');
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.location || !formData.positionType) {
          console.log('Personal Info validation failed: missing required fields');
          toast.error("Please fill in all required fields in Personal Information");
          return false;
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          console.log('Personal Info validation failed: invalid email');
          toast.error("Please enter a valid email address");
          return false;
        }
        console.log('Personal Info validation passed');
        break;

      case 1: // AI Experience
        console.log('Validating AI Experience step...');
        console.log('AI Experience Level:', formData.aiExperienceLevel);
        console.log('AI Tools Used:', formData.aiToolsUsed);
        console.log('Favorite AI Tool:', formData.favoriteAITool);
        console.log('AI Project Description:', formData.aiProjectDescription);
        console.log('AI Project Description Length:', formData.aiProjectDescription.length);

        if (!formData.aiExperienceLevel) {
          console.log('AI Experience validation failed: missing experience level');
          toast.error("Please select your AI experience level");
          return false;
        }
        
        if (formData.aiToolsUsed.length === 0) {
          console.log('AI Experience validation failed: no AI tools selected');
          toast.error("Please select at least one AI tool you have used");
          return false;
        }
        
        if (!formData.favoriteAITool) {
          console.log('AI Experience validation failed: missing favorite AI tool');
          toast.error("Please specify your favorite AI tool");
          return false;
        }
        
        if (!formData.aiProjectDescription) {
          console.log('AI Experience validation failed: missing AI project description');
          toast.error("Please describe a specific AI project or use case");
          return false;
        }
        
        if (formData.aiProjectDescription.length < 50) {
          console.log('AI Experience validation failed: AI project description too short');
          console.log('Current length:', formData.aiProjectDescription.length);
          toast.error(`AI Project description must be at least 50 characters long. Current length: ${formData.aiProjectDescription.length}/50`);
          return false;
        }
        
        console.log('AI Experience validation passed');
        break;

      case 2: // Technical Info
        console.log('Validating Technical Info step...');
        const isTechnicalRole = formData.positionType.includes('developer') ||
                               formData.positionType.includes('engineer') ||
                               formData.positionType === 'data-engineer' ||
                               formData.positionType === 'devops-engineer' ||
                               formData.positionType === 'ai-engineer';

        console.log('Is technical role:', isTechnicalRole);
        console.log('Position type:', formData.positionType);

        if (isTechnicalRole) {
          if (!formData.githubUrl || !formData.linkedinUrl) {
            console.log('Technical Info validation failed: missing GitHub or LinkedIn for technical role');
            toast.error("GitHub and LinkedIn profiles are required for technical positions");
            return false;
          }
          if (formData.technicalSkills.length === 0 || formData.preferredLanguages.length === 0) {
            console.log('Technical Info validation failed: missing technical skills or languages');
            toast.error("Please select your technical skills and preferred languages");
            return false;
          }
        } else {
          // For non-technical roles, only LinkedIn is required
          if (!formData.linkedinUrl) {
            console.log('Technical Info validation failed: missing LinkedIn for non-technical role');
            toast.error("LinkedIn profile is required");
            return false;
          }
        }
        console.log('Technical Info validation passed');
        break;

      case 3: // Experience
        console.log('Validating Experience step...');
        if (!formData.currentRole || !formData.experience || !formData.education || !formData.whyJoinUs || !formData.availabilityDate || !formData.workPreference) {
          console.log('Experience validation failed: missing required fields');
          toast.error("Please fill in all required fields in Professional Experience");
          return false;
        }
        if (formData.whyJoinUs.length < 50) {
          console.log('Experience validation failed: why join us too short');
          toast.error("Please provide a more detailed answer about why you want to join us (minimum 50 characters)");
          return false;
        }
        console.log('Experience validation passed');
        break;
    }

    console.log('Validation passed for step:', currentStep);
    return true;
  };

  const nextStep = () => {
    console.log('Next step clicked, current step:', currentStep);
    if (validateCurrentStep()) {
      console.log('Validation passed, moving to next step');
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    } else {
      console.log('Validation failed, staying on current step');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    try {
      setIsSubmitting(true);
      console.log('Submitting careers application:', formData);

      const response = await submitCareersApplication(formData);

      console.log('Application submitted successfully:', response);

      toast("Application submitted successfully! Thank you for your application. We'll review it and get back to you within 5 business days.");

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        location: '',
        positionType: '',
        aiToolsUsed: [],
        aiExperienceLevel: '',
        favoriteAITool: '',
        aiProjectDescription: '',
        githubUrl: '',
        linkedinUrl: '',
        portfolioUrl: '',
        technicalSkills: [],
        preferredLanguages: [],
        currentRole: '',
        experience: '',
        education: '',
        certifications: '',
        whyJoinUs: '',
        availabilityDate: '',
        workPreference: '',
        salaryExpectation: '',
        coverLetter: ''
      });
      setCurrentStep(0);

    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error(error?.message || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Join Our Team at 3 Halves Labs</CardTitle>
          <CardDescription className="text-center">
            We're looking for passionate individuals who want to shape the future of AI-driven platforms
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-600">{steps[currentStep].title}</span>
            </div>
            <Progress value={progress} className="w-full" />
            <div className="flex justify-between text-xs text-gray-500">
              {steps.map((step, index) => (
                <span key={index} className={index <= currentStep ? 'text-blue-600 font-medium' : ''}>
                  {step.title}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
          />
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 0}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>

        {currentStep === steps.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        ) : (
          <Button
            onClick={nextStep}
            className="flex items-center gap-2"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default CareersApplicationForm;